import { useState, useEffect } from 'react';

interface WebViewDetection {
    isWebView: boolean;
    platformName: string | null;
}

/**
 * Hook to detect if the app is running inside an in-app browser (webview)
 * Common in-app browsers: LinkedIn, Facebook, Instagram, Twitter, etc.
 */
export const useWebViewDetection = (): WebViewDetection => {
    const [detection, setDetection] = useState<WebViewDetection>({
        isWebView: false,
        platformName: null,
    });

    useEffect(() => {
        const detectWebView = (): WebViewDetection => {
            const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

            // Check for specific in-app browsers
            const webViewPatterns = [
                { pattern: /LinkedInApp/i, name: 'LinkedIn' },
                { pattern: /FBAN|FBAV/i, name: 'Facebook' },
                { pattern: /Instagram/i, name: 'Instagram' },
                { pattern: /Twitter/i, name: 'Twitter' },
                { pattern: /Line\//i, name: 'Line' },
                { pattern: /Snapchat/i, name: 'Snapchat' },
                { pattern: /WhatsApp/i, name: 'WhatsApp' },
                { pattern: /Telegram/i, name: 'Telegram' },
                { pattern: /Messenger/i, name: 'Messenger' },
                { pattern: /Pinterest/i, name: 'Pinterest' },
                { pattern: /KAKAOTALK/i, name: 'KakaoTalk' },
                { pattern: /WeChat/i, name: 'WeChat' },
            ];

            // Check each pattern
            for (const { pattern, name } of webViewPatterns) {
                if (pattern.test(userAgent)) {
                    return { isWebView: true, platformName: name };
                }
            }

            // Additional checks for generic webviews
            const isGenericWebView =
                // Check if it's a mobile device but not a standalone browser
                (/Mobile|Android|iPhone|iPad/i.test(userAgent) &&
                // Check for webview indicators
                (userAgent.includes('wv') ||
                 userAgent.includes('WebView') ||
                 // Android WebView
                 (userAgent.includes('Android') && !userAgent.includes('Chrome')) ||
                 // iOS WebView (doesn't include Safari)
                 ((/iPhone|iPad/i.test(userAgent)) && !userAgent.includes('Safari'))));

            if (isGenericWebView) {
                return { isWebView: true, platformName: 'In-App Browser' };
            }

            return { isWebView: false, platformName: null };
        };

        setDetection(detectWebView());
    }, []);

    return detection;
};
